<script setup lang="ts">
import { computed, useTemplateRef, type StyleValue } from 'vue'
import { areRectsEqual } from '#shared/lib/geometry/are-rects-equal'
import { areRectsIntersecting } from '#shared/lib/geometry/are-rects-intersecting'
import { useAnimatedValue } from '#shared/lib/misc/use-animated-value'

type StaticVerticalPosition = 'top-inside' | 'top-outside' | 'bottom-inside' | 'bottom-outside' | 'center'
type StaticHorizontalPosition = 'left-inside' | 'left-outside' | 'right-inside' | 'right-outside' | 'center'

export interface FloatingBoxProps {
  targetEl: HTMLElement | null
  scrollContainerEl?: HTMLElement | Document
  verticalPosition?: StaticVerticalPosition | 'outside'
  horizontalPosition?: StaticHorizontalPosition | 'outside'
  verticalOffset?: number
  horizontalOffset?: number
  viewportPadding?: number
}

const {
  targetEl,
  verticalPosition = 'top-inside',
  horizontalPosition = 'right-outside',
  verticalOffset = 0,
  horizontalOffset = 0,
  viewportPadding = 16,
} = defineProps<FloatingBoxProps>()

const rootEl = useTemplateRef('rootEl')

const rootRect = useAnimatedValue(() => rootEl.value?.getBoundingClientRect(), areRectsEqual)
const targetRect = useAnimatedValue(() => targetEl?.getBoundingClientRect(), areRectsEqual)

const calculateStaticRenderedPosition = (
  rootRect: DOMRect,
  targetRect: DOMRect,
  verticalPosition: StaticVerticalPosition,
  horizontalPosition: StaticHorizontalPosition,
  verticalOffset: number,
  horizontalOffset: number
) => {
  let x = 0
  let y = 0

  if (horizontalPosition.startsWith('left')) {
    x = targetRect.left

    if (horizontalPosition.endsWith('outside')) {
      x -= rootRect.width
    }
  }

  if (horizontalPosition.startsWith('right')) {
    x = targetRect.right

    if (horizontalPosition.endsWith('inside')) {
      x -= rootRect.width
    }
  }

  if (horizontalPosition === 'center') {
    x = targetRect.left + targetRect.width / 2 - rootRect.width / 2
  }

  if (verticalPosition.startsWith('top')) {
    y = targetRect.top

    if (verticalPosition.endsWith('outside')) {
      y -= rootRect.height
    }
  }

  if (verticalPosition.startsWith('bottom')) {
    y = targetRect.bottom

    if (verticalPosition.endsWith('inside')) {
      y -= rootRect.height
    }
  }

  if (verticalPosition === 'center') {
    y = targetRect.top + targetRect.height / 2 - rootRect.height / 2
  }

  x += horizontalOffset
  y += verticalOffset

  if (x < viewportPadding) x = viewportPadding
  if (y < viewportPadding) y = viewportPadding
  if (x + rootRect.width > window.innerWidth - viewportPadding) x = window.innerWidth - viewportPadding - rootRect.width
  if (y + rootRect.height > window.innerHeight - viewportPadding)
    y = window.innerHeight - viewportPadding - rootRect.height

  return { x, y }
}

const renderedPosition = computed(() => {
  if (!rootRect.value || !targetRect.value) return { x: -9999, y: -9999 }

  const possibleStaticVerticalPositions: StaticVerticalPosition[] = []
  const possibleStaticHorizontalPositions: StaticHorizontalPosition[] = []

  if (verticalPosition === 'outside') possibleStaticVerticalPositions.push('top-outside', 'bottom-outside')
  else possibleStaticVerticalPositions.push(verticalPosition)

  if (horizontalPosition === 'outside') possibleStaticHorizontalPositions.push('left-outside', 'right-outside')
  else possibleStaticHorizontalPositions.push(horizontalPosition)

  let possiblePosition: { x: number; y: number } = { x: -9999, y: -9999 }

  for (const staticVerticalPosition of possibleStaticVerticalPositions) {
    for (const staticHorizontalPosition of possibleStaticHorizontalPositions) {
      const dynamicVerticalOffset = staticVerticalPosition.startsWith('top')
        ? -Math.abs(verticalOffset)
        : Math.abs(verticalOffset)

      const dynamicHorizontalOffset = staticHorizontalPosition.startsWith('left')
        ? -Math.abs(horizontalOffset)
        : Math.abs(horizontalOffset)

      const position = calculateStaticRenderedPosition(
        rootRect.value,
        targetRect.value,
        staticVerticalPosition,
        staticHorizontalPosition,
        verticalPosition === 'outside' ? dynamicVerticalOffset : verticalOffset,
        horizontalPosition === 'outside' ? dynamicHorizontalOffset : horizontalOffset
      )

      const newRect: DOMRect = {
        x: position.x,
        y: position.y,
        left: position.x,
        top: position.y,
        width: rootRect.value.width,
        height: rootRect.value.height,
        right: position.x + rootRect.value.width,
        bottom: position.y + rootRect.value.height,
        toJSON: () => '{}',
      }

      const areIntersecting = areRectsIntersecting(newRect, targetRect.value)

      if (!areIntersecting) return position

      possiblePosition = position
    }
  }

  return possiblePosition
})

const rootStyle = computed(() => {
  const style: StyleValue = {
    position: 'fixed',
    top: `${renderedPosition.value.y}px`,
    left: `${renderedPosition.value.x}px`,
  }

  return style
})
</script>

<template>
  <div :class="$style.root" ref="rootEl" :style="rootStyle">
    <slot />
  </div>
</template>

<style lang="scss" module>
.root {
  display: flex;
  flex-direction: column;
  z-index: var(--sc-z-index-floating-box);
}
</style>
